export const STATE_KEY = "lapeyre_wp_state";
export const CHANNEL_NAME = "lapeyre_welcome_popin_channel";
export const COOKIE_MAX_AGE_YEAR = 365 * 24 * 3600;

export interface PopinState {
  seen: boolean;
  completed: boolean;
  suppressUntil: number;
  showReminder: boolean;
  bcFallback: (TabMessage & { _t: number }) | null;
}

export type TabMessage =
  | { type: "POPIN_SEEN" }
  | { type: "POPIN_OPEN" }
  | { type: "POPIN_CLOSE" }
  | { type: "REMINDER_CLOSE" }
  | { type: "REMINDER_REOPEN" }
  | { type: "SUPPRESSED" };

export interface PopinContent {
  activation: "yes" | "no" | string;
  anchor: string;
  dateStart: string;
  dateEnd: string;
  descriptionHtml: string;
  discount: string;
  imageDesktop: string;
  imageMobile: string;
  reminder: string;
}

export type PopinStatus = "pending" | "active" | "inactive";

export const DEFAULT_STATE: Readonly<PopinState> = Object.freeze({
  seen: false,
  completed: false,
  suppressUntil: 0,
  showReminder: false,
  bcFallback: null,
});

let _sanitized = false;

export function clearPersistedState(): void {
  _sanitized = false;
  try {
    localStorage.removeItem(STATE_KEY);
  } catch {
    /* ignore */
  }
  try {
    document.cookie = `${STATE_KEY}=;max-age=0;path=/`;
  } catch {
    /* ignore */
  }
}

export function getPopinStatus(content: PopinContent | undefined | null): PopinStatus {
  if (content == null) return "pending";

  if (content.activation !== "yes") {
    clearPersistedState();
    return "inactive";
  }

  try {
    const now = Date.now();
    const start = new Date(content.dateStart).getTime();
    const end = new Date(content.dateEnd).getTime();

    if (isNaN(start) || isNaN(end)) return "inactive";
    if (now < start || now > end) return "inactive";

    return "active";
  } catch {
    return "inactive";
  }
}

export function isPopinActive(content: PopinContent | undefined | null): boolean {
  return getPopinStatus(content) === "active";
}

export function readState(active: boolean): PopinState {
  if (!active) return { ...DEFAULT_STATE };

  try {
    const raw = localStorage.getItem(STATE_KEY);
    if (raw) return { ...DEFAULT_STATE, ...JSON.parse(raw) };
  } catch {
    /* ignore */
  }

  try {
    const match = document.cookie.match(new RegExp(`${STATE_KEY}=([^;]+)`));
    if (match) return { ...DEFAULT_STATE, ...JSON.parse(decodeURIComponent(match[1])) };
  } catch {
    /* ignore */
  }

  return { ...DEFAULT_STATE };
}

export function writeState(active: boolean, patch: Partial<PopinState>): void {
  if (!active) return;

  const next: PopinState = { ...readState(true), ...patch };

  try {
    localStorage.setItem(STATE_KEY, JSON.stringify(next));
  } catch {
    /* ignore */
  }

  const cookiePayload = encodeURIComponent(JSON.stringify({ seen: next.seen, completed: next.completed }));
  try {
    document.cookie = `${STATE_KEY}=${cookiePayload};max-age=${COOKIE_MAX_AGE_YEAR};path=/`;
  } catch {
    /* ignore */
  }
}

export function sanitizeStateOnMount(active: boolean): void {
  if (!active) return;
  if (_sanitized) return; // already ran this page load — don't touch seen again
  _sanitized = true;

  const state = readState(true);
  const freshBcFallback = state.bcFallback && Date.now() - state.bcFallback._t < 2_000;

  writeState(active, {
    // Don't reset seen—it should persist across page reloads
    // Only clear old bcFallback messages
    bcFallback: freshBcFallback ? state.bcFallback : null,
  });
}

export function resetSanitizeFlag(): void {
  _sanitized = false;
}

export function readAndClearBcFallback(active: boolean): (TabMessage & { _t: number }) | null {
  if (!active) return null;
  const state = readState(true);
  if (!state.bcFallback) return null;
  writeState(active, { bcFallback: null });
  return state.bcFallback;
}

export const hasSeen = (active: boolean) => (active ? readState(true).seen : false);
export const isCompleted = (active: boolean) => (active ? readState(true).completed : false);
export const isSuppressed = (active: boolean) => (active ? readState(true).suppressUntil > Date.now() : true);
export const shouldShowReminder = (active: boolean) => (active ? readState(true).showReminder : false);

export const markSeen = (active: boolean) => writeState(active, { seen: true });
export const markCompleted = (active: boolean) => writeState(active, { completed: true });
export const setSuppression = (active: boolean) =>
  writeState(active, { suppressUntil: Date.now() + 72 * 60 * 60 * 1000 });
export const setReminderVisible = (active: boolean) => writeState(active, { showReminder: true });
export const clearReminderVisible = (active: boolean) => writeState(active, { showReminder: false });

export function getChannel(): BroadcastChannel | null {
  try {
    return new BroadcastChannel(CHANNEL_NAME);
  } catch {
    return null;
  }
}

export function broadcast(active: boolean, msg: TabMessage): void {
  if (!active) return;
  try {
    const ch = getChannel();
    ch?.postMessage(msg);
    ch?.close();
  } catch {
    /* ignore */
  }
  writeState(active, { bcFallback: { ...msg, _t: Date.now() } });
}
